import React, { useEffect, useState, useCallback } from 'react';
import { servicesManager } from '../App';
import { PanoAPI, DentistrySegAPI, LocalAPIXRAD } from '../../../../constants/baseurl';
import axios from 'axios';
import { getBase64 } from '../../../../utils/bucket';
import { Brain, Zap, Loader2 } from 'lucide-react';

function EmptyViewport() {
  const { hangingProtocolService, uiNotificationService, viewportGridService, displaySetService } =
    servicesManager.services;
  const urlParams = new URLSearchParams(window.location.search);
  const patientName = urlParams.get('PatientName');

  const [isPano, setIsPano] = useState(false);
  const [panoURL, setPanoURL] = useState('');
  const [panoImages, setPanoImages] = useState([]);
  const [isSharp, setIsSharp] = useState(false);
  const [segmentedImage, setSegmentedImage] = useState(null);
  const [showSegmented, setShowSegmented] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDentistryFour, setDentistryFour] = useState(false);
  const [isPanoLoading, setIsPanoLoading] = useState(false);

  // Function to fetch panoramic images from S3 after processing
  const fetchPanoFromS3 = useCallback(async (bucketId, studyInstanceUID, isXRAD) => {
    try {
      const base64 = await getBase64(bucketId);

      const { activeViewportId, viewports } = viewportGridService.getState();
      let SeriesInstanceUID = null;

      if (!SeriesInstanceUID && viewports) {
        for (const viewport of viewports.values()) {
          const { displaySetInstanceUIDs } = viewport;
          if (displaySetInstanceUIDs && displaySetInstanceUIDs.length > 0) {
            const displaySet = displaySetService.getDisplaySetByUID(displaySetInstanceUIDs[0]);
            if (displaySet?.SeriesInstanceUID) {
              SeriesInstanceUID = displaySet.SeriesInstanceUID;
              break;
            }
          }
        }
      }

      console.log(SeriesInstanceUID, 'SeriesInstanceUID');

      // Make API call to fetch the processed images from S3
      const fetchEndpoint = `${PanoAPI}/get_pano_s3`;
      const response = await axios.get(fetchEndpoint, {
        params: {
          base64,
          StudyInstanceUID: studyInstanceUID,
          region: 'us-east-1',
        },
      });

      if (
        response.status === 200 &&
        response.data &&
        response.data.status === 'success' &&
        response.data.pano_images
      ) {
        const images = response.data.pano_images;
        setPanoImages(images);
        setPanoURL(images[0]);
        return true;
      }
      return false;
    } catch (error) {
      // Don't log 404 errors as they're expected when images don't exist yet
      if (error.response?.status !== 404) {
        console.error('Error fetching panoramic images from S3:', error);
      }
      return false;
    }
  }, []);

  const getPano = useCallback(async () => {
    setIsPano(true);
    setIsPanoLoading(true);
    try {
      const isXRAD =
        window.location.href.includes('/xrad/') || window.location.href.includes('/radx/');
      const urlParams = new URLSearchParams(window.location.search);
      const bucketIdXrad = urlParams.get('bucketId');
      // Prefer URL param (XRAD embed), then localStorage, then URL search param as fallback
      const bucketId =
        isXRAD
          ? bucketIdXrad
          : (localStorage.getItem('bucketId') || urlParams.get('bucketId') || null);
      const StudyInstanceUID = urlParams.get('StudyInstanceUIDs');
      const isLocal = patientName?.includes(StudyInstanceUID) || false;

      // Get SeriesInstanceUID from active viewport instead of URL params
      const { activeViewportId, viewports } = viewportGridService.getState();
      let SeriesInstanceUID = null;

      // Try active viewport first
      if (activeViewportId && viewports.has(activeViewportId)) {
        const { displaySetInstanceUIDs } = viewports.get(activeViewportId);
        if (displaySetInstanceUIDs && displaySetInstanceUIDs.length > 0) {
          const displaySet = displaySetService.getDisplaySetByUID(displaySetInstanceUIDs[0]);
          SeriesInstanceUID = displaySet?.SeriesInstanceUID;
        }
      }

      // If not found (e.g. active viewport is empty), search in other viewports
      if (!SeriesInstanceUID && viewports) {
        for (const viewport of viewports.values()) {
          const { displaySetInstanceUIDs } = viewport;
          if (displaySetInstanceUIDs && displaySetInstanceUIDs.length > 0) {
            const displaySet = displaySetService.getDisplaySetByUID(displaySetInstanceUIDs[0]);
            if (displaySet?.SeriesInstanceUID) {
              SeriesInstanceUID = displaySet.SeriesInstanceUID;
              break;
            }
          }
        }
      }

      // Fallback to URL param if viewport doesn't have SeriesInstanceUID
      if (!SeriesInstanceUID) {
        SeriesInstanceUID = urlParams.get('SeriesInstanceUID');
      }

      if (isLocal) {
        // Local scenario - send inp_dicom_dir parameter
        const localPath = patientName; // Assuming patientName contains the local directory path
        const apiEndpoint = `${PanoAPI}/panoramic`;
        const response = await axios.get(apiEndpoint, {
          params: {
            inp_dicom_dir: localPath,
            region: 'us-east-1',
            StudyInstanceUID,
            SeriesInstanceUID,
          },
        });

        // Handle local response
        if (response.status === 200 && response.data) {
          if (response.data.success && response.data.msg) {
            // Local response contains base64 encoded images in msg array
            const dataurls = response.data.msg;
            if (dataurls && dataurls.length >= 2) {
              // Extract base64 strings from data URLs
              const extractBase64 = dataUrl => {
                const base64Index = dataUrl.indexOf('base64,');
                return base64Index !== -1 ? dataUrl.substring(base64Index + 7) : dataUrl;
              };

              const normalImage = extractBase64(dataurls[0]); // panoramic_1.png
              const sharpImage = extractBase64(dataurls[1]); // panoramic_1sh.png

              setPanoImages([normalImage, sharpImage]);
              setPanoURL(normalImage);
            } else {
              setPanoURL(null);
            }
          } else {
            setPanoURL(null);
          }
        } else {
          setPanoURL(null);
        }
      } else {
        // AWS scenario - first try to fetch existing images from S3
        console.log('AWS scenario: First checking S3 for existing panoramic images...');

        let existingImages = false;
        try {
          existingImages = await fetchPanoFromS3(bucketId, StudyInstanceUID, isXRAD);
        } catch (error) {
          console.log('Could not check S3 for existing images, will generate new ones');
        }

        if (existingImages) {
          // Images found in S3, use them directly
          uiNotificationService.show({
            title: 'Panoramic View',
            message: 'Existing panoramic images loaded from S3!',
            type: 'success',
            duration: 3000,
          });
          return;
        }

        // Images not found in S3, generate them using panoramic API
        console.log('Images not found in S3, generating new panoramic images...');

        uiNotificationService.show({
          title: 'Panoramic Processing',
          message: 'Generating panoramic images... This may take a few moments.',
          type: 'info',
          duration: 4000,
        });

        try {
          const base64 = await getBase64(bucketId);
          const apiEndpoint = `${PanoAPI}/panoramic`;
          console.log(SeriesInstanceUID, 'SeriesInstanceUID');
          const response = await axios.get(apiEndpoint, {
            params: {
              base64,
              StudyInstanceUID,
              SeriesInstanceUID,
              region: 'us-east-1',
            },
          });

          if (response.status === 200 && response.data && response.data.status === 'success') {
            // Files uploaded to S3, now fetch them with retry logic
            console.log('Files uploaded to S3:', response.data.msg);

            // Wait a moment for processing to complete, then try to fetch
            setTimeout(async () => {
              const fetchSuccess = await fetchPanoFromS3(bucketId, StudyInstanceUID, isXRAD);

              if (!fetchSuccess) {
                // If first attempt fails, try again after a longer delay
                setTimeout(async () => {
                  const secondFetchSuccess = await fetchPanoFromS3(
                    bucketId,
                    StudyInstanceUID,
                    isXRAD
                  );

                  if (!secondFetchSuccess) {
                    uiNotificationService.show({
                      title: 'Panoramic View',
                      message:
                        'Panoramic images are still processing. Please refresh the page in a moment.',
                      type: 'warning',
                      duration: 5000,
                    });
                    setPanoURL(null);
                  } else {
                    uiNotificationService.show({
                      title: 'Panoramic View',
                      message: 'Panoramic images generated and loaded successfully!',
                      type: 'success',
                      duration: 3000,
                    });
                  }
                  setIsPanoLoading(false);
                }, 3000);
              } else {
                uiNotificationService.show({
                  title: 'Panoramic View',
                  message: 'Panoramic images generated and loaded successfully!',
                  type: 'success',
                  duration: 3000,
                });
                setIsPanoLoading(false);
              }
            }, 2000);

            // Don't set loading to false immediately for AWS scenario
            return;
          } else {
            setPanoURL(null);
          }
        } catch (panoError) {
          console.error('Error generating panoramic images:', panoError);
          uiNotificationService.show({
            title: 'Panoramic Generation Error',
            message: 'Failed to generate panoramic images.',
            type: 'error',
            duration: 4000,
          });
          setPanoURL(null);
        }
      }
    } catch (err) {
      setPanoURL(null);
      console.error('Error fetching panoramic images:', err);

      // Show specific error message based on error type
      const errorMessage =
        err.response?.data?.message || err.message || 'Failed to generate panoramic view';
      uiNotificationService.show({
        title: 'Panoramic View Error',
        message: errorMessage,
        type: 'error',
        duration: 4000,
      });
    } finally {
      setIsPanoLoading(false);
    }
  }, [fetchPanoFromS3, patientName, uiNotificationService, viewportGridService, displaySetService]);

  useEffect(() => {
    const hpInfo = hangingProtocolService.getState();
    if (hpInfo.protocolId === 'dentistryFour') {
      getPano();
      setDentistryFour(true);
    }
  }, [getPano, hangingProtocolService]);

  useEffect(() => {
    if (panoURL && isPano) {
      setPanoURL(isSharp ? panoImages[1] : panoImages[0]);
    }
  }, [isSharp, panoURL, isPano, panoImages]);

  useEffect(() => {
    // Only show the "not available" notification after the API call has finished
    // (isPanoLoading=false) AND the result is explicitly null.
    // panoURL initialises as '' (empty string, not null), so this block does
    // NOT fire on first render — only when getPano() explicitly sets it to null.
    if (panoURL === null && !isPanoLoading) {
      uiNotificationService.show({
        title: 'Panoramic View',
        message: 'Panoramic View is not available for this dataset.',
        type: 'warning',
        duration: 3000,
      });
    }
  }, [panoURL, isPanoLoading, uiNotificationService]);

  // Convert base64 string to blob
  const base64ToBlob = (base64String, contentType = 'image/png') => {
    const byteCharacters = atob(base64String);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: contentType });
  };

  const handleAI = async () => {
    if (!panoURL) {
      return;
    }

    setIsLoading(true);

    try {
      // Convert base64 string to blob properly
      const imageBlob = base64ToBlob(panoURL, 'image/png');

      const formData = new FormData();
      formData.append('image', imageBlob, 'pano_image.png');

      const response = await axios.post(`${DentistrySegAPI}/pano_inference`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 30000, // 30 seconds timeout
      });

      if (response.status === 200 && response.data?.image) {
        setSegmentedImage(response.data.image);
        setShowSegmented(true);
        uiNotificationService.show({
          title: 'AI Segmentation',
          message: 'AI segmentation completed successfully!',
          type: 'success',
          duration: 3000,
        });
      }
    } catch (error) {
      console.error('Error while processing image:', error);
      uiNotificationService.show({
        title: 'AI Segmentation Error',
        message: 'Failed to process image with AI segmentation.',
        type: 'error',
        duration: 4000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const toggleImage = () => {
    setShowSegmented(!showSegmented);
  };

  return (
    <div className="relative h-full">
      {isDentistryFour && (
        <>
          {isPanoLoading && (
            <div className="flex h-full items-center justify-center bg-gray-900">
              <div className="rounded-lg border border-white/20 bg-white/10 p-6 backdrop-blur-md">
                <div className="flex items-center gap-3 text-white">
                  <Loader2 className="h-6 w-6 animate-spin" />
                  <span className="text-lg font-medium">Loading Panoramic View...</span>
                </div>
              </div>
            </div>
          )}
          {!isPanoLoading && isPano && panoURL && (
            <div className="group relative h-full w-full overflow-hidden">
              <img
                src={
                  showSegmented && segmentedImage
                    ? segmentedImage
                    : 'data:image/png;base64,' + panoURL
                }
                alt="Panoramic View"
                className="h-full w-full object-cover"
              />

              {/* Control buttons container */}
              <div className="absolute top-4 right-4 flex flex-col gap-2 opacity-80 transition-opacity duration-300 group-hover:opacity-100">
                {/* AI Segmentation Button */}
                <button
                  onClick={segmentedImage ? toggleImage : handleAI}
                  disabled={isLoading}
                  className={`transform rounded-lg border p-2 backdrop-blur-sm transition-all duration-300 ${
                    segmentedImage
                      ? showSegmented
                        ? 'border-blue-400 bg-blue-500/20 text-blue-400 shadow-lg shadow-blue-500/25'
                        : 'border-white/20 bg-white/10 text-white hover:scale-105 hover:bg-white/20 hover:shadow-lg'
                      : 'border-white/20 bg-white/10 text-white hover:scale-105 hover:border-blue-400 hover:bg-blue-500/20 hover:text-blue-400 hover:shadow-lg'
                  } ${isLoading ? 'cursor-not-allowed opacity-50' : ''} active:scale-95`}
                  title={
                    isLoading
                      ? 'Processing...'
                      : segmentedImage
                        ? showSegmented
                          ? 'Show Original Image'
                          : 'Show AI Segmentation'
                        : 'Run AI Segmentation'
                  }
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Brain className="h-4 w-4" />
                  )}
                </button>

                {/* Sharp/Dull Toggle Button */}
                <button
                  onClick={() => setIsSharp(!isSharp)}
                  className={`transform rounded-lg border p-2 backdrop-blur-sm transition-all duration-300 ${
                    isSharp
                      ? 'border-amber-400 bg-amber-500/20 text-amber-400 shadow-lg shadow-amber-500/25'
                      : 'border-white/20 bg-white/10 text-white hover:border-amber-400 hover:bg-amber-500/20 hover:text-amber-400'
                  } hover:scale-105 hover:shadow-lg active:scale-95`}
                  title={isSharp ? 'Switch to Normal View' : 'Switch to Sharp View'}
                >
                  <Zap
                    className={`h-4 w-4 transition-transform duration-300 ${isSharp ? 'rotate-12 scale-110' : ''}`}
                  />
                </button>
              </div>

              {/* AI Processing overlay */}
              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                  <div className="rounded-lg border border-white/20 bg-white/10 p-6 backdrop-blur-md">
                    <div className="flex items-center gap-3 text-white">
                      <Loader2 className="h-6 w-6 animate-spin" />
                      <span className="text-lg font-medium">Processing with AI...</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          {!isPanoLoading && panoURL === null && (
            <p className="flex h-full items-center justify-center text-black dark:text-white">
              Failed to load. Dataset isn&apos;t compatible with Panoramic View.
            </p>
          )}
        </>
      )}
    </div>
  );
}

export default EmptyViewport;
